import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const sub = (p, a, b) => clamp((p - a) / (b - a));
const eio = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const eo = (t) => 1 - Math.pow(1 - t, 3);
const SCENE_START = 0.62;
/* Easing for the scroll tween, deliberately gentler than the cubic driving the
   scrubbed animations: at its midpoint an ease-in-out cubic is travelling at 3×
   its average speed, and on the long steps that peak is what reads as a lurch.
   Sine peaks at about 1.57×. */
const easeStep = (t) => 0.5 * (1 - Math.cos(Math.PI * t));
/* How long a state change takes. The steps are nowhere near evenly spaced — the
   opening one covers 45% of the timeline, roughly six times the shortest — so a
   fixed duration made that one move six times faster than the rest, which read
   as abrupt however it was eased. Scaling by the square root of the distance
   keeps the apparent speed close to constant without making long moves drag. */
const STEP_MS_BASE = 200;
const STEP_MS_PER_ROOT_PX = 11;
const STEP_MS_MIN = 260;
const STEP_MS_MAX = 720;
const stepDuration = (dist) =>
  clamp(STEP_MS_BASE + STEP_MS_PER_ROOT_PX * Math.sqrt(Math.abs(dist)), STEP_MS_MIN, STEP_MS_MAX);
/* How long input is ignored after a step. The cooldown still has to outlast the
   momentum tail of a trackpad flick — at 140ms the tail was landing a second
   step — but it is no longer what paces someone scrolling continuously, because
   sustained input cuts it short (see #isFreshInput). Waiting it out was costing
   nearly a second of dead scrolling per state. */
const STEP_COOLDOWN = 320;
/* Telling a fresh push apart from the tail of the gesture we already consumed.
   A momentum tail only decays, so once a step has been running past the grace
   period, an event still carrying most of the gesture's peak force is someone
   actively scrolling — and they get the next state as soon as the current
   transition ends rather than waiting out a cooldown aimed at the tail. */
const FRESH_INPUT_GRACE = 200;
const FRESH_INPUT_RATIO = 0.6;
const FRESH_INPUT_FLOOR = 6;
/* Finger travel per state. Re-armed after each step, so one long drag walks
   through several states instead of one. */
const TOUCH_STEP_DISTANCE = 24;
const SCENE_END = 1.0;

class FeaturedCollectionScroll extends Component {
  requiredRefs = ['sticky'];

  #cards = [];
  #scenes = [];
  #motifs = [];
  #heading = null;
  #tagline = null;
  #scrollPrompt = null;
  #progressBar = null;
  #cardsLayer = null;
  #frontIdx = 0;
  #spread = 1;
  #frontScale = 5.2;
  #observer = null;
  #rafId = 0;
  #scrolling = false;
  #lastP = -1;
  #onScroll = null;
  #onResize = null;

  /* ---- Discrete step scrolling ---- */
  #steps = [];
  #isStepping = false;
  #tweenRaf = 0;
  #lockedUntil = 0;
  #lockStartedAt = 0;
  #lockPeakDelta = 0;
  #queuedDir = 0;
  #touchStartY = 0;
  #touchReleased = false;
  #stepListenersAttached = false;

  connectedCallback() {
    super.connectedCallback();

    this.#cards = Array.from(this.querySelectorAll('[data-fcs-card]'));
    this.#scenes = Array.from(this.querySelectorAll('[data-fcs-scene]'));
    this.#motifs = Array.from(this.querySelectorAll('[data-fcs-motif]'));
    this.#heading = this.querySelector('[data-fcs-heading]');
    this.#tagline = this.querySelector('[data-fcs-tagline]');
    this.#scrollPrompt = this.querySelector('[data-fcs-scroll-prompt]');
    this.#progressBar = this.querySelector('[data-fcs-progress-bar]');
    this.#cardsLayer = this.querySelector('[data-fcs-cards-layer]');

    this.#spread = parseFloat(this.dataset.spread || '1') || 1;
    this.#frontScale = parseFloat(this.dataset.frontScale || '5.2') || 5.2;

    const featuredFlag = this.#cards.findIndex((c) => c.dataset.fcsFeatured === 'true');
    if (featuredFlag >= 0) {
      this.#frontIdx = featuredFlag;
    } else {
      this.#frontIdx = Math.min(this.#cards.length - 1, Math.floor((this.#cards.length - 1) / 2) + 1);
      if (this.#frontIdx < 0) this.#frontIdx = 0;
    }

    if (prefersReducedMotion()) {
      this.dataset.reducedMotion = 'true';
      this.#applyStaticLayout();
      return;
    }

    this.#onScroll = this.#requestUpdate.bind(this);
    this.#onResize = this.#requestUpdate.bind(this);

    // Must precede the observer: attaching the step listeners needs the steps.
    this.#buildSteps();

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.toggleAttribute('data-onscreen', entry.isIntersecting);
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.#onScroll, { passive: true });
          // The wheel/touch listeners are non-passive so they can cancel the
          // native scroll. They only exist while the section is on screen, so
          // the rest of the page never pays for them.
          this.#attachStepListeners();
          this.#requestUpdate();
        } else {
          window.removeEventListener('scroll', this.#onScroll);
          this.#detachStepListeners();
        }
      },
      { rootMargin: '20% 0% 20% 0%' }
    );
    this.#observer.observe(this);

    window.addEventListener('resize', this.#onResize);
    this.#requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    if (this.#onResize) window.removeEventListener('resize', this.#onResize);
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    if (this.#tweenRaf) cancelAnimationFrame(this.#tweenRaf);
    this.#detachStepListeners();
  }

  /* =====================================================================
     Discrete step scrolling
     The section is one long scrubbed timeline, but visitors move through it
     one state at a time: while the canvas is pinned, a scroll gesture is
     taken over and eased to the next step instead of scrubbing freely, and
     further input is ignored until that transition finishes — so nobody ends
     up parked halfway through an animation. These are the progress (0–1)
     values of each state:
       0     – the intro (heading + tagline), where the section pins
       0.45  – the cards are fully fanned open
       …     – one stop per scene, at its clearest frame
       1     – the end of the timeline, where the section unpins
     There is deliberately no stop between the fan and the first scene. The
     featured card expands across 0.45–0.62 while fading out, and the first
     scene only begins at 0.62, so every value in between is mid-animation:
     the old 0.52 stop parked the card 28% expanded at 72% opacity with no
     scene yet visible. The end of the expansion is no better — at 0.62 the
     card has faded to nothing and the scene has not started, which is a
     blank frame — so the fan steps straight through to the first scene.
     Stepping past either end is not consumed, so the page scrolls on
     normally and visitors are never trapped in the section.
     ===================================================================== */
  #buildSteps() {
    const points = [0];
    if (this.#cards.length > 0) {
      points.push(0.45);
    }
    const sceneCount = this.#scenes.length;
    if (sceneCount > 0) {
      const span = (SCENE_END - SCENE_START) / sceneCount;
      for (let i = 0; i < sceneCount; i += 1) {
        points.push(SCENE_START + (i + 0.55) * span);
      }
    }
    points.push(1);
    this.#steps = points
      .sort((a, b) => a - b)
      .filter((v, i, arr) => i === 0 || v > arr[i - 1] + 0.001);
  }

  #attachStepListeners() {
    if (this.#stepListenersAttached || this.#steps.length < 2) return;
    this.#stepListenersAttached = true;
    window.addEventListener('wheel', this.#handleWheel, { passive: false });
    window.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    window.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    window.addEventListener('keydown', this.#handleKeyDown);
  }

  #detachStepListeners() {
    if (!this.#stepListenersAttached) return;
    this.#stepListenersAttached = false;
    window.removeEventListener('wheel', this.#handleWheel);
    window.removeEventListener('touchstart', this.#handleTouchStart);
    window.removeEventListener('touchmove', this.#handleTouchMove);
    window.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * The next step in a direction, or null at either end of the timeline.
   * Chosen by position rather than by index so a visitor who arrives
   * mid-timeline (scrollbar drag, anchor link, restored scroll) still moves
   * to the adjacent state rather than jumping back to a remembered one.
   * @param {number} dir
   * @returns {number | null}
   */
  #nextStep(dir) {
    const p = this.#progress();
    const EPSILON = 0.004;
    const target =
      dir > 0
        ? this.#steps.find((step) => step > p + EPSILON)
        : [...this.#steps].reverse().find((step) => step < p - EPSILON);
    return target ?? null;
  }

  /**
   * Whether input is currently being held off: during a transition, and for a
   * short cooldown afterwards. The cooldown is a fixed window that blocked
   * events never extend — an earlier version waited for the wheel to fall
   * quiet instead, which stonewalled anyone scrolling continuously (their
   * events never left a gap, so no step ever fired).
   */
  #isLocked() {
    return this.#isStepping || performance.now() < this.#lockedUntil;
  }

  /**
   * Whether a blocked wheel event is a fresh push rather than the decaying tail
   * of the gesture that triggered the current step. The reference is the peak
   * force seen since that step began, not the event that started it — a flick
   * ramps up, so its first event is often one of its smallest, and comparing
   * against that would read the middle of the same flick as new input.
   * @param {number} mag
   * @returns {boolean}
   */
  #isFreshInput(mag) {
    const sustained =
      mag >= FRESH_INPUT_FLOOR &&
      mag >= this.#lockPeakDelta * FRESH_INPUT_RATIO &&
      performance.now() - this.#lockStartedAt > FRESH_INPUT_GRACE;
    // After the test: a rising event is measured against the previous peak.
    if (mag > this.#lockPeakDelta) this.#lockPeakDelta = mag;
    return sustained;
  }

  /**
   * Act on input that arrived while locked: mid-transition it is held until
   * that transition lands, otherwise it cuts the cooldown short.
   * @param {number} dir
   */
  #handleFreshInput(dir) {
    if (this.#isStepping) {
      this.#queuedDir = dir;
      return;
    }
    this.#lockedUntil = 0;
    this.#step(dir);
  }

  /**
   * Ease to the next step. Returns false when there is none, meaning the
   * gesture belongs to the page and must not be swallowed.
   * @param {number} dir
   * @returns {boolean}
   */
  #step(dir) {
    const target = this.#nextStep(dir);
    if (target == null) return false;
    this.#tweenTo(this.#scrollYForProgress(target));
    return true;
  }

  /** @param {WheelEvent} event */
  #handleWheel = (event) => {
    if (!this.#isEngaged()) return;
    if (Math.abs(event.deltaY) < 2) return;

    if (this.#isLocked()) {
      // Mid-transition, or in the cooldown after one: hold the timeline where
      // it is, unless the visitor is still actively scrolling — then take the
      // input rather than throwing away a second of their gesture.
      event.preventDefault();
      if (this.#isFreshInput(Math.abs(event.deltaY))) {
        this.#handleFreshInput(event.deltaY > 0 ? 1 : -1);
      }
      return;
    }

    if (this.#step(event.deltaY > 0 ? 1 : -1)) event.preventDefault();
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    this.#touchStartY = event.touches[0]?.clientY ?? 0;
    this.#touchReleased = false;
  };

  /** @param {TouchEvent} event */
  #handleTouchMove = (event) => {
    if (!this.#isEngaged() || this.#touchReleased) return;

    if (this.#isLocked()) {
      // Held, but the finger keeps travelling — the distance covered while
      // locked still counts towards the next step once the lock lifts.
      event.preventDefault();
      return;
    }

    const delta = this.#touchStartY - (event.touches[0]?.clientY ?? 0);
    if (Math.abs(delta) < 4) return;

    const dir = delta > 0 ? 1 : -1;
    // Decide before swallowing anything: cancelling the first touchmove kills
    // the gesture for good, so at the ends of the timeline we must stay out of
    // the way from the very first pixel or the visitor cannot swipe away.
    if (this.#nextStep(dir) == null) {
      this.#touchReleased = true;
      return;
    }

    event.preventDefault();
    if (Math.abs(delta) >= TOUCH_STEP_DISTANCE) {
      this.#step(dir);
      // Re-arm from where the finger is now, so a drag that keeps going walks
      // on to the next state instead of stalling until the finger lifts.
      this.#touchStartY = event.touches[0]?.clientY ?? this.#touchStartY;
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (!this.#isEngaged()) return;

    const active = document.activeElement;
    if (active instanceof HTMLElement && (active.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName))) {
      return;
    }

    const forward = event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ';
    const backward = event.key === 'ArrowUp' || event.key === 'PageUp';
    if (!forward && !backward) return;

    if (this.#isLocked()) {
      event.preventDefault();
      return;
    }

    if (this.#step(forward ? 1 : -1)) event.preventDefault();
  };

  // The pinned canvas fully covers the viewport — i.e. we're mid-timeline.
  #isEngaged() {
    if (this.dataset.reducedMotion === 'true') return false;
    const rect = this.getBoundingClientRect();
    return rect.top <= 0 && rect.bottom >= window.innerHeight;
  }

  #progress() {
    const rect = this.getBoundingClientRect();
    const total = this.offsetHeight - window.innerHeight;
    if (total <= 0) return 0;
    return clamp(-rect.top / total);
  }

  #scrollYForProgress(p) {
    const rect = this.getBoundingClientRect();
    const total = this.offsetHeight - window.innerHeight;
    const sectionTop = window.scrollY + rect.top;
    return sectionTop + p * total;
  }

  #tweenTo(targetY, duration) {
    if (this.#tweenRaf) cancelAnimationFrame(this.#tweenRaf);
    const startY = window.scrollY;
    const dist = targetY - startY;
    if (Math.abs(dist) < 1) return;
    const ms = duration ?? stepDuration(dist);
    const t0 = performance.now();
    this.#isStepping = true;
    this.#lockStartedAt = t0;
    this.#lockPeakDelta = 0;
    this.#queuedDir = 0;
    const step = (now) => {
      const t = clamp((now - t0) / ms);
      // behavior:'instant' is load-bearing. The theme sets scroll-behavior:
      // smooth on <html>, which applies to programmatic scrolls too: each
      // frame's scrollTo was handing the browser a new target to animate
      // towards and cancelling the previous one, so this easing never reached
      // the page. What played instead was a single native smooth scroll to the
      // final position — measurably, a plain scrollTo moved 0px on the frame it
      // was called, and the eased curve below was decorative.
      window.scrollTo({ top: startY + dist * easeStep(t), behavior: 'instant' });
      if (t < 1) {
        this.#tweenRaf = requestAnimationFrame(step);
      } else {
        this.#isStepping = false;
        // Cooldown so the tail of the gesture that triggered this step cannot
        // immediately trigger the next one — skipped when the visitor scrolled
        // on through the transition, since that input was theirs, not the tail.
        const queued = this.#queuedDir;
        this.#queuedDir = 0;
        if (queued) {
          this.#lockedUntil = 0;
          this.#step(queued);
        } else {
          this.#lockedUntil = performance.now() + STEP_COOLDOWN;
        }
      }
    };
    this.#tweenRaf = requestAnimationFrame(step);
  }

  #requestUpdate() {
    if (this.#scrolling) return;
    this.#scrolling = true;
    this.#rafId = requestAnimationFrame(() => {
      this.#scrolling = false;
      this.#update();
    });
  }

  #update() {
    const rect = this.getBoundingClientRect();
    const total = this.offsetHeight - window.innerHeight;
    if (total <= 0) return;
    const p = clamp(-rect.top / total, 0, 1);
    if (Math.abs(p - this.#lastP) < 0.0005) return;
    this.#lastP = p;

    this.style.setProperty('--fcs-p', p.toFixed(4));

    const fanP = eio(sub(p, 0.18, 0.45));
    const expandP = eio(sub(p, 0.45, 0.62));

    if (this.#cardsLayer) {
      this.#cardsLayer.style.opacity = String(1 - eo(sub(p, 0.7, 0.85)));
    }

    const total_ = this.#cards.length;
    this.#cards.forEach((card, idx) => {
      const isFront = idx === this.#frontIdx;
      const span = (total_ - 1) * 16 * this.#spread;
      const slotX = total_ === 1 ? 0 : -span / 2 + (idx * span) / (total_ - 1);
      const slotY = (idx % 2 === 0 ? -1 : 1) * 1.2;
      const slotRot = (idx - (total_ - 1) / 2) * 1.6;
      const stackX = (idx - (total_ - 1) / 2) * 0.6;
      const stackY = (idx - (total_ - 1) / 2) * 0.4;
      const stackRot = (idx - (total_ - 1) / 2) * 0.8;

      let tx = lerp(stackX, slotX, fanP);
      let ty = lerp(stackY, slotY, fanP);
      let tr = lerp(stackRot, slotRot, fanP);
      let scale = 1;
      let alpha = 1;

      if (isFront) {
        scale = lerp(1, this.#frontScale, expandP);
        tx = lerp(tx, 0, expandP);
        ty = lerp(ty, 0, expandP);
        tr = lerp(tr, 0, expandP);
        alpha = lerp(1, 0, expandP);
      } else {
        const dir = idx < (total_ - 1) / 2 ? -1 : 1;
        tx = lerp(tx, tx + dir * 30, expandP);
        ty = lerp(ty, ty - 4, expandP);
        alpha = lerp(1, 0, expandP);
      }

      const ambient = Math.sin(p * 8 + idx) * 0.4 * (1 - fanP);

      card.style.setProperty('--card-tx', `${tx}vw`);
      card.style.setProperty('--card-ty', `${ty}vh`);
      card.style.setProperty('--card-rot', `${tr}deg`);
      card.style.setProperty('--card-scale', String(scale));
      card.style.setProperty('--card-ambient', `${ambient}px`);
      card.style.opacity = String(alpha);
      card.style.zIndex = isFront ? '50' : String(10 + idx);
      card.toggleAttribute('data-expanding', isFront && expandP > 0);
    });

    const sceneCount = this.#scenes.length;
    this.#scenes.forEach((scene, idx) => {
      const span = (SCENE_END - SCENE_START) / Math.max(sceneCount, 1);
      const a = SCENE_START + idx * span;
      const b = a + span;
      const inP = eio(sub(p, a, a + span * 0.35));
      const outP = idx === sceneCount - 1 ? 0 : eio(sub(p, b - span * 0.25, b));
      const alpha = inP * (1 - outP);
      const scale = lerp(1.04, 1, inP) * lerp(1, 0.98, outP);
      const ty = lerp(2, 0, inP) + lerp(0, -2, outP);

      scene.style.opacity = String(alpha);
      scene.style.transform = `translateY(${ty}vh) scale(${scale})`;
      scene.style.visibility = alpha < 0.001 ? 'hidden' : 'visible';
    });

    this.#motifs.forEach((motif, i) => {
      const t = clamp(p);
      const x1 = parseFloat(motif.dataset.x1 || '0');
      const y1 = parseFloat(motif.dataset.y1 || '0');
      const x2 = parseFloat(motif.dataset.x2 || '0');
      const y2 = parseFloat(motif.dataset.y2 || '0');
      const x = lerp(x1, x2, t);
      const y = lerp(y1, y2, t);
      const fade = 1 - eo(sub(p, 0.45, 0.7)) * 0.7;
      const wob = Math.sin(p * 6 + i) * 6;
      motif.style.setProperty('--motif-x', `${x}%`);
      motif.style.setProperty('--motif-y', `${y}%`);
      motif.style.setProperty('--motif-wob', `${wob}px`);
      motif.style.opacity = String(fade * 0.55);
    });

    if (this.#tagline) {
      const inP = eio(sub(p, 0.02, 0.12));
      const outP = eio(sub(p, 0.14, 0.22));
      const alpha = inP * (1 - outP);
      const ty = lerp(20, 0, inP) + lerp(0, -10, outP);

      this.#tagline.style.opacity = String(alpha);
      this.#tagline.style.transform = `translate(-50%, ${ty}px)`;
      this.#tagline.style.visibility = alpha < 0.001 ? 'hidden' : 'visible';
    }

    if (this.#heading) {
      const outP = eio(sub(p, 0.14, 0.22));
      const alpha = 1 - outP;
      const ty = lerp(0, -10, outP);
      this.#heading.style.opacity = String(alpha);
      this.#heading.style.transform = `translate(-50%, ${ty}px)`;
      this.#heading.style.visibility = alpha < 0.001 ? 'hidden' : 'visible';
    }

    if (this.#scrollPrompt) {
      const alpha = 1 - eo(sub(p, 0, 0.12));
      this.#scrollPrompt.style.opacity = String(alpha);
      this.#scrollPrompt.style.visibility = alpha < 0.01 ? 'hidden' : 'visible';
    }

    if (this.#progressBar) {
      this.#progressBar.style.width = `${p * 100}%`;
    }
  }

  #applyStaticLayout() {
    // For users preferring reduced motion: render a simple linear stack.
    this.#cards.forEach((card, i) => {
      card.style.position = 'static';
      card.style.opacity = '1';
      card.style.transform = 'none';
      card.style.margin = i === 0 ? '0 auto' : '24px auto';
      card.style.left = 'auto';
      card.style.top = 'auto';
    });
    this.#scenes.forEach((scene) => {
      scene.style.position = 'static';
      scene.style.opacity = '1';
      scene.style.transform = 'none';
      scene.style.marginTop = '32px';
      scene.style.visibility = 'visible';
    });
    if (this.#tagline) this.#tagline.style.visibility = 'hidden';
    if (this.#heading) {
      this.#heading.style.opacity = '1';
      this.#heading.style.transform = 'none';
      this.#heading.style.visibility = 'visible';
    }
    if (this.#scrollPrompt) this.#scrollPrompt.style.visibility = 'hidden';
  }
}

if (!customElements.get('featured-collection-scroll')) {
  customElements.define('featured-collection-scroll', FeaturedCollectionScroll);
}
